import React from 'react';
import {
  Box,
  Modal,
  Typography,
  Paper,
  IconButton,
  Divider,
  Grid,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { useKeyboardShortcuts } from '../contexts/KeyboardShortcutsContext';

interface ShortcutGroup {
  title: string;
  shortcuts: {
    keys: string[];
    description: string;
  }[];
}

const defaultShortcuts: ShortcutGroup[] = [
  {
    title: 'Navigation',
    shortcuts: [
      { keys: ['/'], description: 'Focus search box' },
      { keys: ['?'], description: 'Toggle this help' },
      { keys: ['Esc'], description: 'Close modal / Clear selection' },
      { keys: ['G', 'T'], description: 'Go to Tickets' },
      { keys: ['G', 'C'], description: 'Go to Chat' },
      { keys: ['G', 'D'], description: 'Go to Dashboard' },
    ],
  },
  {
    title: 'Tickets',
    shortcuts: [
      { keys: ['N'], description: 'Create new ticket' },
      { keys: ['Ctrl', 'Enter'], description: 'Submit form / Send message' },
      { keys: ['R'], description: 'Refresh data' },
      { keys: ['←'], description: 'Previous ticket' },
      { keys: ['→'], description: 'Next ticket' },
    ],
  },
  {
    title: 'Actions',
    shortcuts: [
      { keys: ['Ctrl', 'K'], description: 'Command palette' },
      { keys: ['Ctrl', '/'], description: 'Quick actions menu' },
      { keys: ['M'], description: 'Toggle multi-channel sender' },
      { keys: ['I'], description: 'Toggle internal notes' },
    ],
  },
];

const ShortcutsHelp: React.FC = () => {
  const { showHelp, setShowHelp, shortcuts } = useKeyboardShortcuts();

  const allShortcuts = [...defaultShortcuts];

  // Add custom shortcuts if any
  if (shortcuts.length > 0) {
    const customShortcuts = shortcuts.map(s => ({
      keys: [
        s.ctrl ? 'Ctrl' : '',
        s.alt ? 'Alt' : '',
        s.shift ? 'Shift' : '',
        s.key.toUpperCase(),
      ].filter(Boolean),
      description: s.description,
    }));

    allShortcuts.push({
      title: 'Custom',
      shortcuts: customShortcuts,
    });
  }

  return (
    <Modal
      open={showHelp}
      onClose={() => setShowHelp(false)}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Paper
        sx={{
          maxWidth: 600,
          width: '90%',
          maxHeight: '80vh',
          overflow: 'auto',
          backgroundColor: 'var(--bg-elevated)',
          color: 'var(--text-primary)',
          border: '1px solid var(--border-light)',
          boxShadow: 'var(--shadow-xl)',
        }}
      >
        <Box sx={{ p: 3 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2,
            }}
          >
            <Typography variant="h5" fontWeight="bold">
              Keyboard Shortcuts
            </Typography>
            <IconButton
              onClick={() => setShowHelp(false)}
              sx={{ color: 'var(--text-secondary)' }}
            >
              <Close />
            </IconButton>
          </Box>

          <Typography variant="body2" color="var(--text-secondary)" sx={{ mb: 3 }}>
            Press <strong>?</strong> anytime to show this help. Shortcuts work globally except when typing in input fields.
          </Typography>

          <Divider sx={{ mb: 3, borderColor: 'var(--border-light)' }} />

          <Grid container spacing={3}>
            {allShortcuts.map((group, groupIndex) => (
              <Grid size={{ xs: 12, md: 6 }} key={groupIndex}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: 'var(--primary-500)',
                    fontWeight: 600,
                    mb: 2,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                  }}
                >
                  {group.title}
                </Typography>

                <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0 }}>
                  {group.shortcuts.map((shortcut, idx) => (
                    <Box
                      component="li"
                      key={idx}
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        py: 1,
                        borderBottom:
                          idx < group.shortcuts.length - 1
                            ? '1px solid var(--border-light)'
                            : 'none',
                      }}
                    >
                      <Typography variant="body2" color="var(--text-secondary)">
                        {shortcut.description}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        {shortcut.keys.map((key, keyIdx) => (
                          <Box
                            key={keyIdx}
                            component="kbd"
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              minWidth: '24px',
                              height: '24px',
                              px: 1,
                              fontSize: '11px',
                              fontWeight: 600,
                              fontFamily: 'var(--font-mono)',
                              color: 'var(--text-primary)',
                              backgroundColor: 'var(--bg-tertiary)',
                              border: '1px solid var(--border-medium)',
                              borderRadius: 'var(--radius-sm)',
                              boxShadow: 'var(--shadow-xs)',
                            }}
                          >
                            {key}
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Paper>
    </Modal>
  );
};

export default ShortcutsHelp;
